import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '../Accordion/Accordion';
import AccordionItem from '../Accordion/AccordionItem';
import Address from '../Address/Address';
import DataTable from '../Table/DataTable';
import { PortalWithState } from 'react-portal';
import { useHistory } from 'react-router-dom';
import { constantToLabel } from '@ecosconnect/core/src/helpers/string';
import useMode from '../../hooks/useMode';
import testerColumns from './TestersThirdPane.columns';
import TesterModal from './TesterModal';
import { Text } from 'rebass';
import Button from '../Button/Button';
import GaugeEditModal from '../Gauge/GaugeEditModal';
import CredentialEditModal from '../Credential/CredentialEditModal';
import gaugesColumns from './Gauges.columns';
import credentialsColumns from './Credentials.columns';
import { Credential, Gauge, Tester, TesterVendor, TesterWS } from './types';
import CredentialViewModal from '../Credential/CredentialViewModal';
import RequirePerms from '../RequirePerms';
import { useHasPerm } from '@/hooks/usePerms';

type TesterDetailProps = {
  testerDetail: Tester;
  refetch: () => void;
};

const TesterDetail: React.FC<TesterDetailProps> = ({
  testerDetail,
  refetch,
}) => {
  const history = useHistory();
  const mode = useMode();

  const vendors = (testerDetail as TesterWS).vendors ?? [];
  const businessLicenses =
    (testerDetail as TesterWS).profile.credentials.filter(
      (a) => a.type === 'BUSINESS_LICENSE'
    ) ?? [];
  const [testerIds, setTesterIds] = useState(vendors.map((a) => a.id));
  const [vendorId, setVendorId] = useState<string>();
  const [selectedGauge, setSelectedGauge] = useState<Gauge>();
  const [selectedCredential, setSelectedCredential] = useState<Credential>();

  const canUpdateGauges = useHasPerm('gauges', 'UPDATE');
  const canUpdateCredentials = useHasPerm('credentials', 'UPDATE');

  return (
    <>
      <Accordion title="Account">
        {testerDetail.accountNumber && (
          <AccordionItem label="Number" value={testerDetail.accountNumber} />
        )}
        <AccordionItem
          label="Status"
          value={constantToLabel(testerDetail.status)}
          status={testerDetail.status === 'ACTIVE' ? 'success' : 'error'}
          kind="chip"
        />
      </Accordion>
      <Accordion title="Contact">
        <AccordionItem label="Name" value={testerDetail.profile.name} />
        {testerDetail.profile.phones.map((phone, idx) => (
          <AccordionItem
            key={phone.id}
            label={
              phone.subType === 'UNKNOWN'
                ? `Phone ${idx + 1}`
                : `Phone (${phone.subType})`
            }
            value={phone.value}
          />
        ))}
        {testerDetail.profile.emails.map((email, idx) => (
          <AccordionItem key={email.id} label="Email" value={email.value} />
        ))}
        {testerDetail.profile.contactAddress && (
          <AccordionItem
            label="Mailing Address"
            value={<Address address={testerDetail.profile.contactAddress} />}
          />
        )}
        {testerDetail.profile.billingAddress && (
          <AccordionItem
            label="Billing Address"
            value={<Address address={testerDetail.profile.billingAddress} />}
          />
        )}
      </Accordion>
      {testerDetail.notes && (
        <Accordion title="Notes">
          <AccordionItem label="Notes" value={testerDetail.notes} />
        </Accordion>
      )}
      {mode === 'water-supplier' && testerDetail.profile.type === 'COMPANY' && (
        <Accordion title="Business Licenses">
          {!!businessLicenses.length && (
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => (
                <>
                  <DataTable
                    columns={credentialsColumns}
                    items={businessLicenses}
                    onRowClick={(credential: Credential) => {
                      setSelectedCredential(credential);
                      openPortal();
                    }}
                  />
                  {portal(
                    <>
                      {selectedCredential && (
                        <CredentialViewModal
                          credential={selectedCredential}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </PortalWithState>
          )}
          {!businessLicenses.length && (
            <Text variant="body2" color="fieldBorderHover">
              No business licenses on file yet.
            </Text>
          )}
        </Accordion>
      )}
      {testerDetail.profile.type === 'INDIVIDUAL' && (
        <>
          <Accordion
            title="Credentials"
            icon={
              <RequirePerms credentials="CREATE">
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={openPortal}
                      >
                        Credential
                      </Button>
                      {portal(
                        <CredentialEditModal
                          action="create"
                          credential={{
                            profileId: testerDetail.profile.id,
                            type: 'BACKFLOW',
                          }}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              </RequirePerms>
            }
          >
            {!!testerDetail.profile.credentials?.length && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={credentialsColumns}
                      items={testerDetail.profile.credentials}
                      onRowClick={(credential) => {
                        setSelectedCredential(credential);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedCredential && (
                          <CredentialEditModal
                            action="edit"
                            readOnly={!canUpdateCredentials}
                            credential={{
                              ...selectedCredential,
                              profileId: testerDetail.profile.id,
                            }}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!testerDetail.profile.credentials?.length && (
              <Text variant="body2" color="fieldBorderHover">
                There are no credentials yet.
              </Text>
            )}
          </Accordion>
          <Accordion
            title="Gauges"
            icon={
              <RequirePerms gauges="CREATE">
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={openPortal}
                      >
                        Gauge
                      </Button>
                      {portal(
                        <GaugeEditModal
                          action="create"
                          gauge={{
                            profileId: testerDetail.profile.id,
                          }}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              </RequirePerms>
            }
          >
            {!!testerDetail.profile.backflowTester?.gauges?.length && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={gaugesColumns}
                      items={testerDetail.profile.backflowTester?.gauges ?? []}
                      onRowClick={(gauge) => {
                        setSelectedGauge(gauge);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedGauge && (
                          <GaugeEditModal
                            action="edit"
                            gauge={{
                              ...selectedGauge,
                              profileId: testerDetail.profile.id,
                            }}
                            readOnly={!canUpdateGauges}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!testerDetail.profile.backflowTester?.gauges?.length && (
              <Text variant="body2" color="fieldBorderHover">
                There are no gauges yet.
              </Text>
            )}
          </Accordion>
        </>
      )}

      {mode === 'water-supplier' &&
        testerDetail.profile.type === 'COMPANY' &&
        !!vendors.length && (
          <Accordion title="Testers">
            <PortalWithState closeOnEsc>
              {({ openPortal, closePortal, portal }) => {
                const onRowClick = (vendor: TesterVendor) => {
                  setVendorId(vendor.id);
                  openPortal();
                };
                return (
                  <>
                    <DataTable
                      columns={testerColumns}
                      itemIds={testerIds}
                      items={vendors}
                      onRowClick={onRowClick}
                    />
                    {portal(
                      <>
                        {vendorId && (
                          <TesterModal
                            onClose={closePortal}
                            tester={
                              vendors.find((vendor) => vendorId === vendor.id)!
                            }
                          />
                        )}
                      </>
                    )}
                  </>
                );
              }}
            </PortalWithState>
          </Accordion>
        )}
    </>
  );
};

TesterDetail.propTypes = {
  testerDetail: PropTypes.any,
};

export default TesterDetail;
